import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button, EmptyState, Spinner } from '@nokowazzi/ui';
import { PageHeader } from '../components/PageHeader';
import { ConfigCard } from '../features/translation/ConfigCard';
import { GlossarySection } from '../features/translation/GlossarySection';
import { ProviderCard } from '../features/translation/ProviderCard';
import { translationConfigQuery } from '../features/translation/queries';
import { toConfigDraft, PROVIDERS } from '../features/translation/types';
import type { ConfigDraft, ProviderId } from '../features/translation/types';
import { UsageSection } from '../features/translation/UsageSection';

export const Route = createFileRoute('/_admin/translation')({
  component: TranslationScreen,
});

function TranslationScreen() {
  const configQuery = useQuery(translationConfigQuery());
  /**
   * 페이지 공용 설정 드래프트 — 상단 카드(활성/체인/한도)와
   * 프로바이더 카드의 모델 입력이 공유하고, 상단 [저장]으로 일괄 저장한다.
   * 최초 로드 시에만 시드해 편집 중 refetch로 입력이 날아가지 않게 한다.
   */
  const [draft, setDraft] = useState<ConfigDraft | null>(null);

  const data = configQuery.data;

  useEffect(() => {
    if (data !== undefined && draft === null) {
      setDraft(toConfigDraft(data.config));
    }
  }, [data, draft]);

  if (configQuery.isPending || (data !== undefined && draft === null)) {
    return (
      <div className="flex min-h-52 items-center justify-center">
        <Spinner label="번역 설정 불러오는 중" />
      </div>
    );
  }

  if (configQuery.isError || data === undefined || draft === null) {
    return (
      <EmptyState
        title="번역 설정을 불러오지 못했습니다"
        description="잠시 후 다시 시도해 주세요"
        action={
          <Button variant="tertiary" size="sm" onClick={() => void configQuery.refetch()}>
            다시 시도
          </Button>
        }
      />
    );
  }

  const credentialByProvider = new Map(data.credentials.map((cred) => [cred.provider, cred]));

  const handleModelChange = (provider: ProviderId, model: string) => {
    setDraft((prev) => (prev === null ? prev : { ...prev, models: { ...prev.models, [provider]: model } }));
  };

  return (
    <>
      <PageHeader
        title="번역 설정"
        description="AI 번역 프로바이더 · 한도 · 용어집 관리 (저장 후 최대 60초 내 반영)"
      />

      <ConfigCard draft={draft} onChange={setDraft} serverConfig={data.config} />

      <section className="mb-6">
        <h2 className="mb-3 text-title3 text-fg">프로바이더</h2>
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-3">
          {PROVIDERS.map((provider) => (
            <ProviderCard
              key={provider}
              provider={provider}
              credential={credentialByProvider.get(provider)}
              model={draft.models[provider] ?? ''}
              onModelChange={(model) => handleModelChange(provider, model)}
            />
          ))}
        </div>
      </section>

      <UsageSection />

      <GlossarySection entries={data.glossary} />
    </>
  );
}
